import React, { useState, useEffect } from 'react';
import { useAuth } from '../context/AuthContext';
import axios from 'axios';
import { toast } from 'sonner';
import { Loader2, Save, FileText, MessageSquare } from 'lucide-react';
import { Button } from './ui/button';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from './ui/dialog';

const API = process.env.REACT_APP_BACKEND_URL || '';

export default function UserProfileMdPanel({ open, onOpenChange, userId, userName }) {
    const { getAuthHeader } = useAuth();
    const [loading, setLoading] = useState(true);
    const [saving, setSaving] = useState(false);
    const [profileMd, setProfileMd] = useState('');
    const [adminNotes, setAdminNotes] = useState('');

    useEffect(() => {
        if (!open || !userId) return;
        const load = async () => {
            setLoading(true);
            try {
                const res = await axios.get(`${API}/api/admin/users/${userId}/profile-md`, { headers: getAuthHeader() });
                const md = res.data.user_profile_md || '';
                setProfileMd(md);
                // Extract admin notes from markdown
                const match = md.match(/## Admin Notes\n([\s\S]*?)(?=\n## |\s*$)/);
                setAdminNotes(match ? match[1].trim() : '');
            } catch { toast.error('Failed to load user profile'); }
            finally { setLoading(false); }
        };
        load();
    }, [open, userId, getAuthHeader]);

    const handleSave = async () => {
        setSaving(true);
        try {
            const res = await axios.put(`${API}/api/admin/users/${userId}/profile-md`, {
                admin_notes: adminNotes,
            }, { headers: getAuthHeader() });
            setProfileMd(res.data.user_profile_md || '');
            toast.success('User profile updated');
        } catch { toast.error('Failed to save'); }
        finally { setSaving(false); }
    };

    // Strip admin notes section from preview (shown separately in textarea)
    const previewMd = profileMd.replace(/## Admin Notes\n[\s\S]*?(?=\n## |\s*$)/, '').trim();

    return (
        <Dialog open={open} onOpenChange={onOpenChange}>
            <DialogContent className="sm:max-w-2xl max-h-[85vh] overflow-y-auto">
                <DialogHeader>
                    <DialogTitle className="flex items-center gap-2">
                        <FileText className="w-5 h-5 text-stone-700" />
                        User Profile — {userName || 'User'}
                    </DialogTitle>
                    <DialogDescription>AI context document auto-generated from profile and voice data. Admin notes are editable.</DialogDescription>
                </DialogHeader>

                {loading ? (
                    <div className="flex items-center justify-center py-12"><Loader2 className="w-6 h-6 text-stone-400 animate-spin" /></div>
                ) : (
                    <div className="space-y-5">
                        {/* Markdown Preview */}
                        <div>
                            <h4 className="text-xs font-semibold text-stone-500 uppercase tracking-wider mb-2 flex items-center gap-1.5">
                                <FileText className="w-3.5 h-3.5" /> Profile Document (Auto-Generated)
                            </h4>
                            <div className="bg-stone-50 rounded-lg border border-stone-200 p-4 max-h-96 overflow-y-auto">
                                <pre className="whitespace-pre-wrap text-sm text-stone-700 font-sans leading-relaxed">
                                    {previewMd || 'No profile data yet. The user needs to fill in their profile in Settings.'}
                                </pre>
                            </div>
                        </div>

                        {/* Editable Admin Notes */}
                        <div className="border-t border-stone-200 pt-4">
                            <h4 className="text-xs font-semibold text-stone-500 uppercase tracking-wider mb-2 flex items-center gap-1.5">
                                <MessageSquare className="w-3.5 h-3.5" /> Admin Notes (Editable)
                            </h4>
                            <p className="text-xs text-stone-400 mb-2">
                                These notes are included in the AI context document. Use them to add context the AI should know about this rep.
                            </p>
                            <textarea
                                value={adminNotes}
                                onChange={e => setAdminNotes(e.target.value)}
                                rows={5}
                                placeholder="e.g. Strong relationship builder. Excels with enterprise accounts in healthcare. Prefers warm introductions over cold outreach. Currently ramping up in the APAC territory."
                                className="w-full px-3 py-2 text-sm border border-stone-200 rounded-lg bg-white focus:outline-none focus:ring-2 focus:ring-stone-900/20 focus:border-stone-500"
                            />
                        </div>

                        <div className="flex justify-end pt-2 border-t border-stone-100">
                            <Button onClick={handleSave} disabled={saving}>
                                {saving ? <><Loader2 className="w-4 h-4 mr-1.5 animate-spin" /> Saving...</> :
                                <><Save className="w-4 h-4 mr-1.5" /> Save Notes</>}
                            </Button>
                        </div>
                    </div>
                )}
            </DialogContent>
        </Dialog>
    );
}
